/* eslint-disable no-inner-declarations */
import logger from '../../../utils/src/logger';
import { isNodeOrCollection } from '../../../utils/src/dom';
import { isPrimitiveRegion } from './RegionHandling';
var StringPropertyType;
(function (StringPropertyType) {
    function hasChanged(oldValue, newValue) {
        return oldValue !== newValue;
    }
    StringPropertyType.hasChanged = hasChanged;
    function fromAttribute(val) {
        return val;
    }
    StringPropertyType.fromAttribute = fromAttribute;
    function validate(val, propName, definition) {
        if (val === undefined || val === null) {
            if (definition.nullable === false) {
                logger.error("Property \"" + propName + "\" cannot be null or undefined");
            }
            return;
        }
        if (typeof val !== 'string') {
            logger.error("Property \"" + propName + "\" must be a string, got \"" + val + "\" (" + typeof val + ") instead");
            return;
        }
        if (definition.valid && definition.valid.indexOf(val) === -1) {
            logger.error("Property \"" + propName + "\" can only have the following values: " + (definition.valid.join(', ') + " (was \"" + val + "\")"));
            return;
        }
    }
    StringPropertyType.validate = validate;
    function getDefaultValue(definition) {
        return definition.defaultValue;
    }
    StringPropertyType.getDefaultValue = getDefaultValue;
})(StringPropertyType || (StringPropertyType = {}));
var BooleanPropertyType;
(function (BooleanPropertyType) {
    function hasChanged(oldValue, newValue) {
        return oldValue !== newValue;
    }
    BooleanPropertyType.hasChanged = hasChanged;
    function fromAttribute(val, attrName) {
        if (val === 'true') {
            return true;
        }
        else if (val === 'false') {
            return false;
        }
        else {
            logger.error("Attribute \"" + attrName + "\" must be \"true\" or \"false\", got \"" + val + "\" (" + typeof val + ") instead");
        }
    }
    BooleanPropertyType.fromAttribute = fromAttribute;
    function validate(val, propName, propertyDefinition) {
        if (typeof val !== 'boolean' && !(propertyDefinition.nullable && val === null)) {
            var acceptableValues = propertyDefinition.nullable ? 'true, false or null' : 'true or false';
            var message = "Property \"" + propName + "\" must be " + acceptableValues + "; got \"" + val + "\" (" + typeof val + ") instead";
            logger.error(message);
        }
    }
    BooleanPropertyType.validate = validate;
    function getDefaultValue(definition) {
        if (definition.defaultValue !== undefined) {
            return Boolean(definition.defaultValue);
        }
        if (definition.nullable) {
            return null;
        }
        return false;
    }
    BooleanPropertyType.getDefaultValue = getDefaultValue;
})(BooleanPropertyType || (BooleanPropertyType = {}));
var NumberPropertyType;
(function (NumberPropertyType) {
    function hasChanged(oldValue, newValue) {
        return oldValue !== newValue;
    }
    NumberPropertyType.hasChanged = hasChanged;
    function stringIsNumber(val) {
        // If NaN, then not a number
        if (isNaN(val)) {
            return false;
        }
        // Make sure it looks like a normal number (no 1e10, Infinity etc)
        var match = val.match(/^[0-9.-]+$/);
        return !!match;
    }
    function fromAttribute(val, attrName) {
        if (val === null) {
            return null;
        }
        if (!stringIsNumber(val)) {
            logger.error('Attribute "' + attrName + '" does not specify a valid number, was "' + val + '"');
            return;
        }
        return parseFloat(val);
    }
    NumberPropertyType.fromAttribute = fromAttribute;
    function validate(val, propertyName, definition) {
        if (val === null || val === undefined) {
            return null;
        }
        if (typeof val !== 'number') {
            logger.error('Property "' + propertyName + '" must be a number, was "' + val + '" (' + typeof val + ')');
            return;
        }
        if (!isFinite(val)) {
            logger.error('Property "' + propertyName + '" only allows for finite numbers, got "' + val + '" instead');
            return;
        }
    }
    NumberPropertyType.validate = validate;
    function getDefaultValue(definition) {
        return definition.defaultValue;
    }
    NumberPropertyType.getDefaultValue = getDefaultValue;
})(NumberPropertyType || (NumberPropertyType = {}));
var RegionPropertyType;
(function (RegionPropertyType) {
    function hasChanged(oldValue, newValue) {
        return oldValue !== newValue;
    }
    RegionPropertyType.hasChanged = hasChanged;
    function fromAttribute(val) {
        return val;
    }
    RegionPropertyType.fromAttribute = fromAttribute;
    function validate(val, propertyName, definition) {
        if (isPrimitiveRegion(val) || isNodeOrCollection(val)) {
            return;
        }
        logger.error("Property \"" + propertyName + "\" must be a string, number or Node, got \"" + val + "\" (" + typeof val + ") instead");
    }
    RegionPropertyType.validate = validate;
    function getDefaultValue(definition) {
        // Not supported for now -- we would have to clone
        // node here if we want to support default values.
        return;
    }
    RegionPropertyType.getDefaultValue = getDefaultValue;
})(RegionPropertyType || (RegionPropertyType = {}));
var ObjectPropertyType;
(function (ObjectPropertyType) {
    function hasChanged(oldValue, newValue) {
        var differentReference = oldValue !== newValue;
        var anyDefined = newValue || oldValue;
        return differentReference || anyDefined;
    }
    ObjectPropertyType.hasChanged = hasChanged;
    function fromAttribute(val, attrName) {
        // Not supported for now. We could do some kind of JSON translation,
        // but do we really want that?
        logger.error("Attribute \"" + attrName + "\" must be an object, a string has been provided instead.");
    }
    ObjectPropertyType.fromAttribute = fromAttribute;
    function validate(val, propertyName, definition) {
        // FIXME Here we should validate the type passed on in the definition
        // exists and that the object passed on matches the description
    }
    ObjectPropertyType.validate = validate;
    function getDefaultValue(definition) {
        // Not supported for now -- we would have to clone
        // objects here if we want to support default values.
        return;
    }
    ObjectPropertyType.getDefaultValue = getDefaultValue;
})(ObjectPropertyType || (ObjectPropertyType = {}));
var ArrayPropertyType;
(function (ArrayPropertyType) {
    function hasChanged(oldValue, newValue) {
        var differentReference = oldValue !== newValue;
        var anyDefined = newValue || oldValue;
        return differentReference || anyDefined;
    }
    ArrayPropertyType.hasChanged = hasChanged;
    function fromAttribute(val, attrName) {
        // Not supported for now. We could do some kind of JSON translation,
        // but do we really want that?
        logger.error("Attribute \"" + attrName + "\" must be an array, a string has been provided instead.");
    }
    ArrayPropertyType.fromAttribute = fromAttribute;
    function validate(val, propName, definition) {
        if (Array.isArray(val) || (definition.nullable && (val === null || val === undefined))) {
            return;
        }
        logger.error("Property \"" + propName + "\" must be an array, was " + val + " (" + typeof val + ")");
    }
    ArrayPropertyType.validate = validate;
    function getDefaultValue(definition) {
        if (definition.nullable) {
            return;
        }
        return [];
    }
    ArrayPropertyType.getDefaultValue = getDefaultValue;
})(ArrayPropertyType || (ArrayPropertyType = {}));
var FunctionPropertyType;
(function (FunctionPropertyType) {
    function hasChanged(oldValue, newValue) {
        return oldValue !== newValue;
    }
    FunctionPropertyType.hasChanged = hasChanged;
    function fromAttribute(val, attrName) {
        // Not supported for now. We could do some kind of eval,
        // but do we really want that?
        logger.error("Attribute \"" + attrName + "\" must be a function, a string has been provided instead.");
    }
    FunctionPropertyType.fromAttribute = fromAttribute;
    function validate(val, propName, definition) {
        if (val === null || val === undefined) {
            return null;
        }
        if (typeof val !== 'function') {
            logger.error("Property \"" + propName + "\" must be a function, was " + val + " (" + typeof val + ")");
        }
    }
    FunctionPropertyType.validate = validate;
    function getDefaultValue(definition) {
        return definition.defaultValue;
    }
    FunctionPropertyType.getDefaultValue = getDefaultValue;
})(FunctionPropertyType || (FunctionPropertyType = {}));
export default {
    string: StringPropertyType,
    boolean: BooleanPropertyType,
    region: RegionPropertyType,
    object: ObjectPropertyType,
    array: ArrayPropertyType,
    // Integers and floats use the same number type, since
    // typescript only has one number type.
    integer: NumberPropertyType,
    float: NumberPropertyType,
    nullableBoolean: BooleanPropertyType,
    function: FunctionPropertyType
};
//# sourceMappingURL=PropertyTypes.js.map